import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Upload, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, DownloadButton } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { StatusPill, TILES, LateCell, ActionTag } from "@/components/shipments/ShipCommon";
import { ShipmentDrawer } from "@/components/shipments/ShipmentDrawer";
import { AddShipmentDialog } from "@/components/shipments/AddShipmentDialog";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { usePref } from "@/hooks/usePref";
import { dateLabel, fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

const stale = (iso, today) => !iso || (new Date(today) - new Date(iso.slice(0, 10))) / 864e5 > 2;

const Freshness = ({ f, today }) => (
  <div className="mb-4 flex flex-wrap gap-2 text-xs" data-testid="freshness-strip">
    {[["Delhivery status updated", f.Delhivery], ["Blue Dart status updated", f["Blue Dart"]], ["Orders up to", f.orders_upto]].map(([l, v]) => (
      <span key={l} className={cn("rounded-full border px-3 py-1 font-medium", stale(v, today) ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700")}>
        {l}: <b>{v ? dateLabel(v.slice(0, 10)) : "never"}</b></span>
    ))}
  </div>
);

const Tiles = ({ tiles, tile, setTile }) => (
  <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9" data-testid="shipment-tiles">
    {TILES.map(([k, label, tone]) => (
      <button key={k} onClick={() => setTile(k)} data-testid={`tile-${k}`}
        className={cn("rounded-lg border bg-white px-3 py-2.5 text-left transition-colors hover:border-amber-400",
          tile === k ? "border-amber-500 ring-2 ring-amber-400/40" : "border-border")}>
        <div className={cn("text-xl font-extrabold font-mono", tone)}>{fmtNum(tiles[k])}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">{label}</div>
      </button>
    ))}
  </div>
);

const ReturnedBadge = ({ list }) => (list || []).map((x) => (
  <Link key={x.id} to={`/returns/r/${x.id}`} onClick={(e) => e.stopPropagation()} data-testid={`ship-returned-${x.return_id}`}
    className="ml-1 inline-block rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700 hover:underline">Returned · {x.return_id}</Link>
));

const Row = ({ r, onOpen, ret }) => (
  <tr className="cursor-pointer hover:bg-amber-50/40" onClick={() => onOpen(r.id)} data-testid={`ship-row-${r.id}`}>
    <td className="text-xs">{dateLabel(r.order_date)}</td>
    <td><div className="font-mono text-xs">{r.order_id}</div><div className="text-[10px] text-muted-foreground">{r.channel}</div></td>
    <td><div className="max-w-[160px] truncate text-sm">{r.customer_name || "—"}</div><div className="font-mono text-[10px] text-muted-foreground">{r.phone}</div></td>
    <td><div className="max-w-[220px] whitespace-normal line-clamp-2 text-xs" title={r.product}>{r.product}</div></td>
    <td className="text-xs font-medium">{r.courier || "—"}</td>
    <td className="font-mono text-xs">{r.tracking_id || <span className="text-red-600 font-sans">No AWB</span>}<ReturnedBadge list={ret?.[r.tracking_id]} /></td>
    <td><StatusPill status={r.status} /><div className="mt-0.5 max-w-[180px] truncate text-[10px] text-muted-foreground" title={r.courier_remark}>{r.courier_remark}</div></td>
    <td className="text-xs">{dateLabel(r.expected)}</td>
    <td className="num"><LateCell days={r.days_late} /></td>
    <td><ActionTag action={r.action} /><div className="mt-0.5 max-w-[180px] truncate text-[10px] text-muted-foreground" title={r.last_note}>{r.last_note}</div></td>
  </tr>
);

const Card = ({ r, onOpen }) => (
  <button onClick={() => onOpen(r.id)} className="w-full rounded-lg border border-border bg-white p-3 text-left" data-testid={`ship-card-${r.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0"><div className="font-mono text-xs">{r.order_id}</div><div className="truncate text-sm font-semibold">{r.customer_name || "—"}</div></div>
      <StatusPill status={r.status} />
    </div>
    <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.product}</div>
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
      <span>{dateLabel(r.order_date)}</span><span className="font-medium">{r.courier || "—"}</span><span className="font-mono">AWB {r.tracking_id || "—"}</span>
      <span>Exp {dateLabel(r.expected)}</span>{r.days_late ? <span>Late <LateCell days={r.days_late} /></span> : null}
    </div>
    {r.action && <div className="mt-2"><ActionTag action={r.action} /></div>}
  </button>
);

export default function Shipments() {
  const { can } = useAuth();
  const [p, setP] = usePref("shipments-filters", { q: "", courier: "all", channel: "all", days: 120, tile: "need_action", sort: "late", page: 1 });
  const [qInput, setQInput] = useState(p.q || "");
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => { const t = setTimeout(() => setP((x) => (x.q === qInput ? x : { ...x, q: qInput, page: 1 })), 350); return () => clearTimeout(t); }, [qInput, setP]);
  const { data, reload } = useLoad(() => api.get("/shipments", { params: p }).then((r) => r.data), [JSON.stringify(p)]);
  const ret = useLoad(() => api.get("/returns/by-tracking").then((r) => r.data).catch(() => ({})), []).data;
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v, page: 1 }));
  if (!data) return <Loading />;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const { page, ...exportParams } = p;
  return (
    <div>
      <PageHeader title="Shipment Desk" subtitle="Every Amazon and website shipment in one list. Click a row to update status or add a follow-up.">
        {can("shipments", "edit") && <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50" onClick={() => setAdding(true)} data-testid="ship-add-btn"><Plus className="h-4 w-4" /> Add shipment</Button>}
        {can("shipment_uploads", "edit") && <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-600 text-white"><Link to="/shipments/upload" data-testid="ship-upload-link"><Upload className="h-4 w-4" /> Upload files</Link></Button>}
        <DownloadButton path="/shipments/export" params={exportParams} testid="ship-export-btn" />
      </PageHeader>
      <Freshness f={data.freshness} today={data.today} />
      <Tiles tiles={data.tiles} tile={p.tile} setTile={set("tile")} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Order ID, AWB, customer, phone, product" className="pl-8" data-testid="ship-search-input" /></div>
        <SimpleSelect value={p.courier} onChange={set("courier")} testid="ship-courier-select" options={[["all", "All couriers"], ...data.couriers.map((c) => [c, c])]} />
        <SimpleSelect value={p.channel} onChange={set("channel")} testid="ship-channel-select" className="h-9 w-32" options={[["all", "All channels"], ["Amazon", "Amazon"], ["Website", "Website"]]} />
        <SimpleSelect value={String(p.days)} onChange={(v) => set("days")(Number(v))} testid="ship-range-select" className="h-9 w-36" options={[["30", "Last 30 days"], ["60", "Last 60 days"], ["120", "Last 120 days"], ["0", "All dates"]]} />
        <SimpleSelect value={p.sort} onChange={set("sort")} testid="ship-sort-select" className="h-9 w-40" options={[["late", "Most late first"], ["newest", "Newest first"], ["oldest", "Oldest first"]]} />
        <span className="text-xs text-muted-foreground" data-testid="ship-total">{fmtNum(data.total)} shipments</span>
      </div>
      <div className="panel hidden overflow-auto lg:block">
        <table className="data-table" data-testid="ship-table">
          <thead><tr><th>Order date</th><th>Order ID</th><th>Customer</th><th>Product</th><th>Courier</th><th>AWB</th><th>Status</th><th>Expected</th><th className="text-right">Days late</th><th>Action / last note</th></tr></thead>
          <tbody>{data.rows.map((r) => <Row key={r.id} r={r} onOpen={setOpen} ret={ret} />)}</tbody>
        </table>
        {!data.rows.length && <div className="py-10 text-center text-sm text-muted-foreground">No shipments match.</div>}
      </div>
      <div className="space-y-2 lg:hidden" data-testid="ship-cards">{data.rows.map((r) => <Card key={r.id} r={r} onOpen={setOpen} />)}</div>
      <div className="mt-3 flex items-center justify-end gap-2 text-sm">
        <Button size="sm" variant="outline" disabled={p.page <= 1} onClick={() => setP((x) => ({ ...x, page: x.page - 1 }))} data-testid="ship-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
        <span data-testid="ship-page-info">Page {p.page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={p.page >= pages} onClick={() => setP((x) => ({ ...x, page: x.page + 1 }))} data-testid="ship-next-page"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <ShipmentDrawer id={open} statuses={data.statuses} onClose={() => setOpen(null)} onChanged={reload} />
      <AddShipmentDialog open={adding} onOpenChange={setAdding} statuses={data.statuses} onSaved={(id) => { reload(); setOpen(id); }} />
    </div>
  );
}
